import React, { useMemo, useRef, useState } from "react";
import { config as baseConfig, lineConfig } from "../config";
import {
  resolveTokenColor,
  resolveColors,
  getExtents,
  computeNiceTicks,
  formatYLabel,
  formatDataValue,
} from "../SupportFiles/chartUtils";
import {
  ClipPathRect,
  BackgroundRect,
  GridHorizontal,
  GridVertical,
  AxisLines,
} from "../SupportFiles/chartSections";
import { useUniqueComponentId } from "../SupportFiles/uniqueId";
import NoData from "../../Info/NoData";

function xScale(i, n, w, l, r, e) {
  const cw = Math.max(0, w - l - r - 2 * e);
  return l + e + (n > 1 ? (i * cw) / (n - 1) : cw / 2);
}
function yScale(v, min, max, h, t, b, clampToMin) {
  const ch = Math.max(0, h - t - b);
  const vv = clampToMin ? Math.max(min, v) : v;
  const k = (vv - min) / (max - min);
  return t + ch - (isFinite(k) ? k : 0) * ch;
}
function pathFor(data, n, w, h, p, e, clampToMin, curveType) {
  if (!data || data.length === 0) return "";
  if (curveType === "smooth" && data.length >= 2) {
    const pts = [];
    for (let i = 0; i < data.length; i++) {
      const x = xScale(i, n, w, p.left, p.right, e);
      const y = yScale(data[i], p.min, p.max, h, p.top, p.bottom, clampToMin);
      pts.push({ x, y });
    }
    let d = `M${pts[0].x},${pts[0].y}`;
    if (pts.length === 2) {
      d += ` L${pts[1].x},${pts[1].y}`;
      return d;
    }
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[i > 0 ? i - 1 : i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2 < pts.length ? i + 2 : i + 1];
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C${c1x},${c1y} ${c2x},${c2y} ${p2.x},${p2.y}`;
    }
    return d;
  }
  let d = "";
  for (let i = 0; i < data.length; i++) {
    const x = xScale(i, n, w, p.left, p.right, e);
    const y = yScale(data[i], p.min, p.max, h, p.top, p.bottom, clampToMin);
    if (i === 0) {
      d = `M${x},${y}`;
    } else if (curveType === "stepline") {
      const prevY = yScale(
        data[i - 1],
        p.min,
        p.max,
        h,
        p.top,
        p.bottom,
        clampToMin,
      );
      d += ` L${x},${prevY} L${x},${y}`;
    } else {
      d += ` L${x},${y}`;
    }
  }
  return d;
}
function areaPathFor(data, n, w, h, p, e, clampToMin, curveType) {
  if (!data || data.length === 0) return "";
  const top = pathFor(data, n, w, h, p, e, clampToMin, curveType);
  const firstX = xScale(0, n, w, p.left, p.right, e);
  const lastX = xScale(n - 1, n, w, p.left, p.right, e);
  const baseY = h - p.bottom;
  return `${top} L${lastX},${baseY} L${firstX},${baseY} Z`;
}

const LineChartsNew = ({
  series = [{ name: "Series 1", data: [] }],
  categories = [],
  title,
  height = 360,
  width = 600,
  colors,
  curve = lineConfig?.line?.stroke?.curve || "straight",
  strokeWidth = lineConfig?.line?.stroke?.width || 2,
  lineCap = lineConfig?.line?.stroke?.lineCap || "round",
  markersSize = lineConfig?.line?.markers?.size || 4,
  dataLabelsEnabled = baseConfig?.showLabels?.show || false,
  areaShow = lineConfig?.line?.area?.show || false,
  areaFill = lineConfig?.line?.area?.fill || { type: "solid" },
  edgePadding,
  minStepX = lineConfig?.minStepX,
  colorMap,
  padding: paddingProp,
  componentId,
  onHover,
}) => {
  const generatedId = useUniqueComponentId("charts-adv");
  const localId = componentId || generatedId;
  const basePad = (paddingProp ?? baseConfig.padding) || {
    top: 16,
    right: 12,
    bottom: 28,
    left: 40,
  };
  const svgW = typeof width === "number" ? width : Number(width) || 600;
  const svgH = typeof height === "number" ? height : Number(height) || 360;
  const pad = {
    top: basePad.top,
    right: basePad.right,
    bottom: basePad.bottom,
    left: basePad.left,
  };
  const axisLabelFontSizePx =
    typeof baseConfig.axis?.fontSize === "number"
      ? baseConfig.axis.fontSize
      : 12;
  const tickLabelGap = Math.max(8, Math.round(axisLabelFontSizePx * 0.6));
  const resolvedColors = useMemo(
    () => resolveColors(colors || baseConfig.colors),
    [colors],
  );
  const axisLabelColor = resolveTokenColor(baseConfig.axis.labelColor);
  const axisBorderColor = resolveTokenColor(baseConfig.axis.color);
  const gridColor = resolveTokenColor(baseConfig.grid.color);
  const gridBg = resolveTokenColor(baseConfig.grid?.background);
  const clampData = !!baseConfig.axis.y?.clampMinZero;
  const ePad =
    typeof edgePadding === "number"
      ? edgePadding
      : typeof baseConfig.axis?.edgePadding === "number"
        ? baseConfig.axis.edgePadding
        : Math.max(2, Math.ceil(Math.max(strokeWidth, markersSize)));
  const { min, max } = getExtents(series, !!baseConfig.axis.y?.clampMinZero);
  const plotHPre = Math.max(0, svgH - pad.top - pad.bottom);
  const tickTargetPx =
    typeof baseConfig.axis?.y?.tickTargetPx === "number"
      ? baseConfig.axis.y.tickTargetPx
      : 48;
  const interior =
    typeof baseConfig.axis?.y?.gridLines === "number"
      ? baseConfig.axis.y.gridLines
      : Math.max(2, Math.round(plotHPre / tickTargetPx));
  const {
    ticks: ticksY,
    step: tickStep,
    niceMin,
    niceMax,
  } = computeNiceTicks(min, max, interior, !!baseConfig.axis.y?.clampMinZero);
  const p = { ...pad, min: niceMin, max: niceMax };
  const axisYWidth =
    typeof baseConfig.axis?.y?.width === "number"
      ? Math.max(0, baseConfig.axis.y.width)
      : p.left;
  const axisXHeight =
    typeof baseConfig.axis?.x?.height === "number"
      ? Math.max(0, baseConfig.axis.x.height)
      : Math.max(24, axisLabelFontSizePx + 12);
  const nCats = categories.length;
  const minStepXFinal =
    typeof minStepX === "number" ? Math.max(1, minStepX) : 36;
  const containerW = Math.max(1, svgW - axisYWidth);
  const scrollTailPad =
    typeof baseConfig.axis?.scrollTailPad === "number"
      ? Math.max(0, baseConfig.axis.scrollTailPad)
      : Math.max(16, Math.ceil(axisLabelFontSizePx) + ePad);
  const baseContentW =
    p.left +
    p.right +
    2 * ePad +
    Math.max(0, minStepXFinal * Math.max(nCats - 1, 0));
  const scrollEnabled =
    baseConfig.axis?.scroll === "auto"
      ? baseContentW > containerW
      : !!baseConfig.axis?.scroll;
  const wEff = scrollEnabled ? baseContentW + scrollTailPad : containerW;
  const dash = baseConfig.grid?.dasharray || "3 3";
  const axisFontSize =
    typeof baseConfig.axis?.fontSize === "number"
      ? `${baseConfig.axis.fontSize}px`
      : undefined;
  const dataLabelFontSize =
    typeof baseConfig.showLabels?.fontSize === "number"
      ? `${baseConfig.showLabels.fontSize}px`
      : undefined;
  const scrollRef = useRef(null);
  const svgRef = useRef(null);
  const clipIdRef = useRef(`plotClip-${localId}`);
  const [hover, setHover] = useState(null);
  const handleMove = (e) => {
    const r = scrollRef.current?.getBoundingClientRect();
    if (!r || categories.length < 1) return;
    const xMin = p.left + ePad;
    const xMax = wEff - p.right - ePad;
    const yMin = p.top;
    const yMax = svgH - p.bottom;
    const scrollLeft = scrollRef.current?.scrollLeft || 0;
    const x = e.clientX - r.left + scrollLeft;
    const y = e.clientY - r.top;
    if (x < xMin || x > xMax || y < yMin || y > yMax) {
      setHover(null);
      if (onHover) onHover(null);
      return;
    }
    const cw = Math.max(1, xMax - xMin);
    const idx = Math.max(
      0,
      Math.min(
        categories.length - 1,
        Math.round(((x - xMin) / cw) * (categories.length - 1)),
      ),
    );
    const xp = xScale(idx, categories.length, wEff, p.left, p.right, ePad);
    setHover({ i: idx, x: xp });
    if (onHover) {
      const values = series.map((s) => s.data[idx]);
      onHover({
        index: idx,
        guidelineX: r.left + xp - scrollLeft,
        topY: r.top + p.top + 12,
        category: categories[idx],
        values,
        componentId: localId,
      });
    }
  };
  const handleLeave = () => {
    setHover(null);
    if (onHover) onHover(null);
  };
  const draggingRef = useRef(false);
  const [dragging, setDragging] = useState(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollRef = useRef(0);
  const onPointerDown = (e) => {
    const t = scrollRef.current;
    if (!t) return;
    draggingRef.current = true;
    setDragging(true);
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = t.scrollLeft || 0;
    if (e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {}
    }
  };
  const onPointerMove = (e) => {
    if (!draggingRef.current) return;
    const t = scrollRef.current;
    if (!t) return;
    const dx = e.clientX - dragStartXRef.current;
    const max = Math.max(0, t.scrollWidth - t.clientWidth);
    const next = Math.min(Math.max(0, dragStartScrollRef.current - dx), max);
    t.scrollLeft = next;
  };
  const onPointerUp = () => {
    draggingRef.current = false;
    setDragging(false);
  };
  const onTouchStart = (e) => {
    const t = scrollRef.current;
    if (!t) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    draggingRef.current = true;
    setDragging(true);
    dragStartXRef.current = touch.clientX;
    dragStartScrollRef.current = t.scrollLeft || 0;
  };
  const onTouchMove = (e) => {
    if (!draggingRef.current) return;
    e.preventDefault();
    const t = scrollRef.current;
    if (!t) return;
    const touch = e.touches && e.touches[0];
    if (!touch) return;
    const dx = touch.clientX - dragStartXRef.current;
    const max = Math.max(0, t.scrollWidth - t.clientWidth);
    const next = Math.min(Math.max(0, dragStartScrollRef.current - dx), max);
    t.scrollLeft = next;
  };
  const onTouchEnd = () => {
    draggingRef.current = false;
    setDragging(false);
  };
  const onScroll = () => {};
  const dashFromLineConfig = (sIdx) => {
    const dc = lineConfig?.line?.stroke?.dashArray;
    if (Array.isArray(dc)) {
      const v = dc[sIdx];
      if (typeof v === "number") return `${v} ${v}`;
      return v ?? undefined;
    }
    if (typeof dc === "number") return `${dc} ${dc}`;
    return dc || undefined;
  };
  const visibleSeries = useMemo(
    () => series.filter((s) => !s.__projection),
    [series],
  );
  const isEmpty = useMemo(() => {
    if (!Array.isArray(categories) || categories.length === 0) return true;
    if (!Array.isArray(visibleSeries) || visibleSeries.length === 0)
      return true;
    let hasAny = false;
    let hasNonZero = false;
    for (const s of visibleSeries) {
      const arr = Array.isArray(s?.data) ? s.data : [];
      if (arr.length > 0) hasAny = true;
      for (const v of arr) {
        const n = v != null && isFinite(v) ? Number(v) : 0;
        if (n !== 0) {
          hasNonZero = true;
          break;
        }
      }
      if (hasNonZero) break;
    }
    return !hasAny || !hasNonZero;
  }, [categories, visibleSeries]);
  if (isEmpty) {
    const noDataCfg = baseConfig?.noDataConfig || {};
    const showNoData =
      typeof noDataCfg.show === "boolean" ? noDataCfg.show : true;
    return (
      <div
        className="chartWrapper w-full h-full flex"
        style={{
          width: typeof width === "number" ? `${width}px` : width,
          height: typeof height === "number" ? `${height}px` : height,
        }}
      >
        {showNoData ? (
          <NoData
            title={noDataCfg.title}
            desc={noDataCfg.desc}
            color={noDataCfg.color}
            noImage={noDataCfg.noImage}
          />
        ) : (
          <div className="w-full h-full relative p-6">
            <svg
              className="w-full h-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <rect x="0" y="0" width="100" height="100" fill="#ffffff" />
              {[20, 35, 50, 65, 80].map((y) => (
                <line
                  key={`g-${y}`}
                  x1="8"
                  y1={y}
                  x2="98"
                  y2={y}
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              ))}
              {[20, 35, 50, 65, 80, 95].map((x) => (
                <line
                  key={`v-${x}`}
                  x1={x}
                  y1="10"
                  x2={x}
                  y2="90"
                  stroke="#f3f4f6"
                  strokeWidth="1"
                />
              ))}
              <polyline
                points="10,70 24,60 38,64 52,48 66,54 80,36 95,44"
                fill="none"
                stroke="#d1d5db"
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              {[
                [10, 70],
                [24, 60],
                [38, 64],
                [52, 48],
                [66, 54],
                [80, 36],
                [95, 44],
              ].map(([x, y], i) => (
                <circle
                  key={`p-${i}`}
                  cx={x}
                  cy={y}
                  r="1.6"
                  fill="#f9fafb"
                  stroke="#d1d5db"
                  strokeWidth="0.8"
                />
              ))}
              <line x1="8" y1="10" x2="8" y2="90" stroke="#e5e7eb" />
              <line x1="8" y1="90" x2="98" y2="90" stroke="#e5e7eb" />
            </svg>
            <div className="absolute bottom-4 left-0 right-0 text-center text-sm text-gray-500">
              No data available
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="chartWrapper w-full h-full grid grid-cols-[auto_1fr]">
      <div className="yAxis h-full">
        <svg width={axisYWidth} height={svgH}>
          <g>
            {ticksY.map((tv, i) => {
              const y = yScale(tv, p.min, p.max, svgH, p.top, p.bottom, false);
              return (
                <text
                  key={`yl-${i}`}
                  x={axisYWidth - tickLabelGap}
                  y={y + 4}
                  fill={axisLabelColor}
                  fontSize={axisFontSize}
                  textAnchor="end"
                >
                  {formatYLabel(tv, tickStep)}
                </text>
              );
            })}
          </g>
        </svg>
      </div>
      <div className="chartContainer flex flex-col" style={{ minWidth: 0 }}>
        <div
          ref={scrollRef}
          style={{
            overflowX: scrollEnabled ? "auto" : "hidden",
            overflowY: "hidden",
            width: containerW,
            cursor: scrollEnabled
              ? dragging
                ? "grabbing"
                : "grab"
              : "default",
            userSelect: dragging ? "none" : "auto",
            touchAction: "none",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onScroll={onScroll}
        >
          <svg
            ref={svgRef}
            width={wEff}
            height={svgH}
            onMouseMove={handleMove}
            onMouseLeave={handleLeave}
          >
            <ClipPathRect
              id={clipIdRef.current}
              x={p.left}
              y={p.top}
              width={wEff - p.left - p.right}
              height={svgH - p.top - p.bottom}
            />
            <BackgroundRect
              x={p.left}
              y={p.top}
              width={wEff - p.left - p.right}
              height={svgH - p.top - p.bottom}
              fill={gridBg}
            />
            <g clipPath={`url(#${clipIdRef.current})`}>
              {baseConfig.grid.horizontal && (
                <GridHorizontal
                  ys={ticksY
                    .filter((_, idx) => idx > 0 && idx < ticksY.length - 1)
                    .map((tv) =>
                      yScale(tv, p.min, p.max, svgH, p.top, p.bottom, false),
                    )}
                  left={p.left}
                  right={wEff - p.right}
                  color={gridColor}
                  dash={dash}
                  strokeWidth={baseConfig.grid.strokeWidth || 1}
                />
              )}
              {baseConfig.grid.vertical && (
                <GridVertical
                  xs={categories.map((_, i) =>
                    Math.round(
                      xScale(i, categories.length, wEff, p.left, p.right, ePad),
                    ),
                  )}
                  top={p.top}
                  bottom={svgH - p.bottom}
                  color={gridColor}
                  dash={dash}
                  strokeWidth={baseConfig.grid.strokeWidth || 1}
                />
              )}
            </g>
            <g>
              <AxisLines
                xAxis={{ x1: p.left, x2: wEff - p.right, y: svgH - p.bottom }}
                yAxis={{ x: p.left, y1: p.top, y2: svgH - p.bottom }}
                color={axisBorderColor}
              />
            </g>
            <g clipPath={`url(#${clipIdRef.current})`}>
              {series.map((s, si) => {
                const color =
                  (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                  resolvedColors[
                    ((s && s.__idx) ?? si) % resolvedColors.length
                  ] ||
                  "#3b82f6";
                const seriesCurve = s.__projection
                  ? baseConfig.projection?.curve || curve
                  : curve;
                const areaShowFinal = s.__projection
                  ? !!baseConfig?.projection?.area?.show
                  : typeof areaShow === "boolean"
                    ? areaShow
                    : !!lineConfig?.line?.area?.show;
                const areaFillCfg = s.__projection
                  ? baseConfig?.projection?.area?.fill || { type: "solid" }
                  : areaFill ||
                    lineConfig?.line?.area?.fill || { type: "solid" };
                const dArea =
                  areaShowFinal &&
                  areaPathFor(
                    s.data,
                    s.data.length,
                    wEff,
                    svgH,
                    p,
                    ePad,
                    clampData,
                    seriesCurve,
                  );
                const d = pathFor(
                  s.data,
                  s.data.length,
                  wEff,
                  svgH,
                  p,
                  ePad,
                  clampData,
                  seriesCurve,
                );
                const strokeDash = s.__projection
                  ? baseConfig.projection?.dasharray || undefined
                  : dashFromLineConfig((s && s.__idx) ?? si);
                const strokeW = s.__projection
                  ? typeof baseConfig.projection?.strokeWidth === "number"
                    ? baseConfig.projection.strokeWidth
                    : strokeWidth
                  : strokeWidth;
                const cap = s.__projection
                  ? baseConfig.projection?.lineCap || lineCap
                  : lineCap;
                return (
                  <g key={si}>
                    {areaShowFinal && dArea && (
                      <path
                        d={dArea}
                        fill={
                          areaFillCfg.color
                            ? resolveTokenColor(areaFillCfg.color)
                            : s.__projection
                              ? resolveTokenColor(
                                  baseConfig?.projection?.color,
                                ) || color
                              : color
                        }
                        opacity={
                          typeof areaFillCfg.opacity === "number"
                            ? areaFillCfg.opacity
                            : 0.25
                        }
                        stroke="none"
                      />
                    )}
                    <path
                      d={d}
                      fill="none"
                      stroke={color}
                      strokeWidth={strokeW}
                      strokeLinecap={cap}
                      strokeDasharray={strokeDash}
                      vectorEffect="non-scaling-stroke"
                    />
                    {markersSize > 0 &&
                      !s.__projection &&
                      s.data.map((v, i) => {
                        const x = xScale(
                          i,
                          s.data.length,
                          wEff,
                          p.left,
                          p.right,
                          ePad,
                        );
                        const y = yScale(
                          v,
                          p.min,
                          p.max,
                          svgH,
                          p.top,
                          p.bottom,
                          clampData,
                        );
                        return (
                          <circle
                            key={i}
                            cx={x}
                            cy={y}
                            r={markersSize}
                            fill={color}
                          />
                        );
                      })}
                    {dataLabelsEnabled &&
                      !s.__projection &&
                      s.data.map((v, i) => {
                        const x = xScale(
                          i,
                          s.data.length,
                          wEff,
                          p.left,
                          p.right,
                          ePad,
                        );
                        const y =
                          yScale(
                            v,
                            p.min,
                            p.max,
                            svgH,
                            p.top,
                            p.bottom,
                            clampData,
                          ) - 8;
                        const showCfg = baseConfig.showLabels || {};
                        const valueDisplay = showCfg.valueDisplay || "value";
                        const textValue =
                          valueDisplay === "percentage"
                            ? ""
                            : formatDataValue(v);
                        const labelColor =
                          showCfg.colorMode === "single" && showCfg.color
                            ? resolveTokenColor(showCfg.color)
                            : color;
                        return (
                          <text
                            key={i}
                            x={x}
                            y={y}
                            fill={labelColor}
                            fontSize={dataLabelFontSize}
                            textAnchor="middle"
                          >
                            {textValue}
                          </text>
                        );
                      })}
                  </g>
                );
              })}
            </g>
            {hover && (
              <g clipPath={`url(#${clipIdRef.current})`}>
                <line
                  x1={hover.x}
                  x2={hover.x}
                  y1={p.top}
                  y2={svgH - p.bottom}
                  stroke={gridColor}
                  strokeDasharray={dash}
                />
                {series.map((s, si) => {
                  const v = s.data[hover.i];
                  if (v == null) return null;
                  if (s.__projection) return null;
                  const color =
                    (colorMap && colorMap[(s && s.__idx) ?? si]) ||
                    resolvedColors[
                      ((s && s.__idx) ?? si) % resolvedColors.length
                    ] ||
                    "#3b82f6";
                  const y = yScale(
                    v,
                    p.min,
                    p.max,
                    svgH,
                    p.top,
                    p.bottom,
                    clampData,
                  );
                  return (
                    <circle
                      key={si}
                      cx={hover.x}
                      cy={y}
                      r={Math.max(3, markersSize)}
                      fill={color}
                      stroke="#fff"
                    />
                  );
                })}
              </g>
            )}
            {title && (
              <text
                x={p.left}
                y={p.top - 6}
                fill={axisLabelColor}
                fontSize={axisFontSize}
              >
                {title}
              </text>
            )}
          </svg>
          <div className="xAxis" style={{ width: wEff }}>
            <svg width={wEff} height={axisXHeight}>
              <g>
                {categories.map((c, i) => {
                  const x = Math.round(
                    xScale(i, categories.length, wEff, p.left, p.right, ePad),
                  );
                  return (
                    <text
                      key={`cx-${i}`}
                      x={x}
                      y={axisXHeight - 6}
                      fill={axisLabelColor}
                      fontSize={axisFontSize}
                      textAnchor="middle"
                    >
                      {c}
                    </text>
                  );
                })}
              </g>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineChartsNew;
